import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ProjectService from "../ProjectService";
import Highcharts from "../point.extensions";
import HighchartsReact from "highcharts-react-official";
import "../AllProjects.scss";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import {
  calculateDate,
  getTimeZoneName,
  getDecimalValue,
  checkAbsoluteValue,
} from "../../../config/helpingFunctions";
import computeDifferential from "./functions/DifferentialFilter";
import computeIntegration from "./functions/IntegrationFilter";

const SingleReport = (): JSX.Element => {
  const { t } = useTranslation();

  const search = useLocation().search;
  const did = new URLSearchParams(search).get("did") as string;
  const startTime = new URLSearchParams(search).get("str_time") as string;
  const endTime = new URLSearchParams(search).get("end_time") as string;
  const din = new URLSearchParams(search).get("din") as string;

  const [dinVisible, setDinVisible] = useState<boolean>(false);

  const [xData, setXdata] = useState<any>([]);
  const [yData, setYdata] = useState<any>([]);
  const [zData, setZdata] = useState<any>([]);

  const [dinXData, setDinXData] = useState<any>([]);
  const [dinYData, setDinYData] = useState<any>([]);
  const [dinZData, setDinZData] = useState<any>([]);

  const [xAxisUnit, setXAxisUnit] = useState<any>();
  const [yAxisUnit, setYAxisUnit] = useState<any>();
  const [zAxisUnit, setZAxisUnit] = useState<any>();

  const [deviceName, setDeviceName] = useState<string>("");
  const [deviceId, setDeviceId] = useState<string>("");
  const [gpsPos, setGpsPos] = useState<string>("");
  const [strtTime, setStrtTime] = useState<any>("");
  const [edTime, setEndTime] = useState<any>("");

  const [maxX, setMaxX] = useState<number>();
  const [maxY, setMaxY] = useState<number>();
  const [maxZ, setMaxZ] = useState<number>();

  const [maxTimeX, setMaxTimeX] = useState<any>();
  const [maxTimeY, setMaxTimeY] = useState<any>();
  const [maxTimeZ, setMaxTimeZ] = useState<any>();

  const [frequenceX, setFrequenceX] = useState<number>();
  const [frequenceY, setFrequenceY] = useState<number>();
  const [frequenceZ, setFrequenceZ] = useState<number>();

  const [integrationX, setIntegrationX] = useState<any>();
  const [integrationY, setIntegrationY] = useState<any>();
  const [integrationZ, setIntegrationZ] = useState<any>();

  const [differentialX, setDifferentialX] = useState<any>();
  const [differentialY, setDifferentialY] = useState<any>();
  const [differentialZ, setDifferentialZ] = useState<any>();

  const [headerData, setHeaderData] = useState<any>({});

  const projectService = new ProjectService();

  const [xConfig, setxConfig] = useState<any>({
    title: {
      text: "",
    },
  });
  const [yConfig, setyConfig] = useState<any>({
    title: {
      text: "",
    },
  });
  const [zConfig, setzConfig] = useState<any>({
    title: {
      text: "Relative Time in [s]",
    },
  });

  const graph_x = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: true,
      // Chart-level boost when there are more than 5 series in the chart
      seriesThreshold: 5,
    },
    chart: {
      zoomType: "x",
      height: 220,
      width: 900,
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      labels: { enabled: false },
      ...JSON.parse(JSON.stringify(xConfig)),
    },
    yAxis: {
      title: {
        text: xAxisUnit,
      },
    },
    title: {
      text: "X-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: xData,
        showInLegend: false,
        name: "Amplitude mm/s",
        enableMouseTracking: false,
      },
    ],
  };

  const graph_y = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: true,
      // Chart-level boost when there are more than 5 series in the chart
      seriesThreshold: 5,
    },
    chart: {
      zoomType: "x",
      height: 220,
      width: 900,
    },

    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      labels: { enabled: false },
      ...JSON.parse(JSON.stringify(yConfig)),
    },
    yAxis: {
      title: {
        text: yAxisUnit,
      },
    },
    title: {
      text: "Y-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: yData,
        showInLegend: false,
        name: "Amplitude mm/s",
        enableMouseTracking: false,
      },
    ],
  };

  const graph_z = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: true,
      // Chart-level boost when there are more than 5 series in the chart
      seriesThreshold: 5,
    },
    chart: {
      zoomType: "x",
      height: 220,
      width: 900,
    },

    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      crosshair: true,
      ...JSON.parse(JSON.stringify(zConfig)),
    },
    yAxis: {
      title: {
        text: zAxisUnit,
      },
    },
    title: {
      text: "Z-Axis Graph",
    },
    subtitle: {
      text: "",
    },
    series: [
      {
        data: zData,
        showInLegend: false,
        name: "Amplitude mm/s",
        enableMouseTracking: false,
      },
    ],
  };

  const scatter_graph = {
    credits: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: true,
      // Chart-level boost when there are more than 5 series in the chart
      seriesThreshold: 5,
    },
    chart: {
      type: "scatter",
      zoomType: "xy",
      // height: auto,
      width: 500,
    },
    title: {
      text: t("din_plot"),
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      title: {
        enabled: true,
        text: "Frequency [Hz]",
      },
      max: 100,
      min: 0,
    },
    yAxis: {
      title: {
        text: "Velocity [mm/s]",
      },
      max: 50,
      min: 0,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          states: {
            hover: {
              enabled: false,
              //   lineColor: 'rgb(100,100,100)'
            },
          },
        },
      },
    },
    series: [
      {
        type: "line",
        name: "1",
        data: [
          [0, 20],
          [10, 20],
          [50, 40],
          [200, 50],
        ],
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 0,
          },
        },
        enableMouseTracking: false,
      },
      {
        type: "line",
        name: "2",
        color: "rgba(252, 144, 3)",
        data: [
          [0, 5],
          [10, 5],
          [50, 15],
          [100, 20],
        ],
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 0,
          },
        },
        enableMouseTracking: false,
      },
      {
        type: "line",
        name: "3",
        color: "rgba(252, 186, 3)",
        data: [
          [0, 3],
          [10, 3],
          [50, 8],
          [100, 10],
        ],
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 0,
          },
        },
        enableMouseTracking: false,
      },
      {
        name: "K1(X)",
        color: "rgba(235, 64, 52)",
        data: [dinXData],
        enableMouseTracking: false,
      },
      {
        name: "K2(Y)",
        color: "rgba(52, 70, 235)",
        data: [dinYData],
        enableMouseTracking: false,
      },
      {
        name: "K3(Z)",
        color: "rgba(235, 52, 104)",
        data: [dinZData],
        enableMouseTracking: false,
      },
    ],
  };

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem("lang")!);

    if (din === "true") {
      projectService
        .getDinData(did, parseInt(startTime), parseInt(endTime))
        .then((response) => {
          let x_data: any;
          let y_data: any;
          let z_data: any;

          x_data = [
            Math.round(response["max_X"] * 100) / 100,
            response["frequence_X"],
          ];
          y_data = [
            Math.round(response["max_Y"] * 100) / 100,
            response["frequence_Y"],
          ];
          z_data = [
            Math.round(response["max_Z"] * 100) / 100,
            response["frequence_Z"],
          ];

          setDinXData(x_data);
          setDinYData(y_data);
          setDinZData(z_data);

          setDinVisible(true);
        });
    }

    const apiCalls = [
      projectService
        .getVisualizationData(did, startTime, endTime)
        .then((response) => {
          let x_data: any;
          let y_data: any;
          let z_data: any;
          let freq_X: any, freq_Y: any, freq_Z: any;

          /**  FOR TIME SECONDS **/
          response.map((data: any) => {
            if (data["channel_X"]) {
              const timeX = (
                checkAbsoluteValue(data["channel_X"], data["max"]) /
                Number(data["header_data"]["sampling_rate"])
              ).toFixed(2);
              const updatedTimeX: any = getDecimalValue(timeX);
              setMaxTimeX(updatedTimeX);
              const integerX: any = getDecimalValue(
                Number(
                  computeIntegration(
                    data["channel_X"],
                    Number(data["descaling"]),
                    data["header_data"]["sampling_rate"]
                  )
                )
              );
              setIntegrationX(integerX);
              const differenX: any = getDecimalValue(
                Number(
                  computeDifferential(
                    data["channel_X"],
                    Number(data["descaling"]),
                    data["header_data"]["sampling_rate"]
                  )
                )
              );
              setDifferentialX(differenX);
              setDeviceName(data.header_data["device_name"]);
              setHeaderData({
                station_name: data.header_data["station_name"],
                client: data.header_data["client"],
                company: data.header_data["company"],
                location: data.header_data["location"],
                station: data["station"],
              });
              const date = calculateDate(
                new Date(data.start_time).getTime() / 1000
              );
              const eddate = calculateDate(
                new Date(data.end_time).getTime() / 1000
              );
              setStrtTime(date);
              setEndTime(eddate);
              setDeviceId(data.did);
              const pos: any = getDecimalValue(data.gps_pos);
              setGpsPos(pos);
              setXAxisUnit(data.unit);
              const maxX: any = getDecimalValue(Number(data.max).toFixed(3));
              setMaxX(maxX);
              if (data.frequence_X === null) {
                freq_X = "";
              } else {
                freq_X = getDecimalValue(Number(data.frequence_X));
              }
              setFrequenceX(freq_X);
              x_data = data.channel_X.map((x: any, i: any) => [
                (data.time_interval / data.channel_X.length) * i,
                x,
              ]);
            } else if (data["channel_Y"]) {
              const timeY = (
                checkAbsoluteValue(data["channel_Y"], data["max"]) /
                Number(data["header_data"]["sampling_rate"])
              ).toFixed(2);
              const updatedTimeY: any = getDecimalValue(timeY);
              setMaxTimeY(updatedTimeY);
              setYAxisUnit(data.unit);
              const integerY: any = getDecimalValue(
                Number(
                  computeIntegration(
                    data["channel_Y"],
                    Number(data["descaling"]),
                    data["header_data"]["sampling_rate"]
                  )
                )
              );
              setIntegrationY(integerY);
              const differenY: any = getDecimalValue(
                Number(
                  computeDifferential(
                    data["channel_Y"],
                    Number(data["descaling"]),
                    data["header_data"]["sampling_rate"]
                  )
                )
              );
              setDifferentialY(differenY);
              const maxY: any = getDecimalValue(Number(data.max).toFixed(3));
              setMaxY(maxY);
              if (data.frequence_Y === null) {
                freq_Y = "";
              } else {
                freq_Y = getDecimalValue(Number(data.frequence_Y));
              }
              setFrequenceY(freq_Y);
              y_data = data.channel_Y.map((x: any, i: any) => [
                (data.time_interval / data.channel_Y.length) * i,
                x,
              ]);
            } else if (data["channel_Z"]) {
              const timeZ = (
                checkAbsoluteValue(data["channel_Z"], data["max"]) /
                Number(data["header_data"]["sampling_rate"])
              ).toFixed(2);
              const updatedTimeZ: any = getDecimalValue(timeZ);
              setMaxTimeZ(updatedTimeZ);
              setZAxisUnit(data.unit);
              const integerZ: any = getDecimalValue(
                Number(
                  computeIntegration(
                    data["channel_Z"],
                    Number(data["descaling"]),
                    data["header_data"]["sampling_rate"]
                  )
                )
              );
              setIntegrationZ(integerZ);
              const differenZ: any = getDecimalValue(
                Number(
                  computeDifferential(
                    data["channel_Z"],
                    Number(data["descaling"]),
                    data["header_data"]["sampling_rate"]
                  )
                )
              );
              setDifferentialZ(differenZ);
              const maxZ: any = getDecimalValue(Number(data.max).toFixed(3));
              setMaxZ(maxZ);
              if (data.frequence_Z === null) {
                freq_Z = "";
              } else {
                freq_Z = getDecimalValue(Number(data.frequence_Z));
              }
              setFrequenceZ(freq_Z);
              z_data = data.channel_Z.map((x: any, i: any) => [
                (data.time_interval / data.channel_Z.length) * i,
                x,
              ]);
            }
          });

          setXdata(x_data);
          setYdata(y_data);
          setZdata(z_data);
        }),
    ];

    Promise.all(apiCalls).then(() => {
      setTimeout(window.print, 1000);
      window.addEventListener("afterprint", () => window.close());
    });
  }, [i18n.language]);

  return (
    <div>
      <img
        alt="DMT-Logo"
        src="./images/logo.png"
        className="mr-2"
        style={{
          float: "right",
          paddingRight: "40px",
          height: "32px",
        }}
      />

      <h2 style={{ textAlign: "center", paddingTop: "10px" }}>
        {t("event_report")}
      </h2>
      <div>
        <div className="grid">
          <div className="lg:col-6">
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem">{t("date_time")}:</h3>
              <span className="device-details ml-7">
                {" "}
                {strtTime} [{getTimeZoneName()}]
              </span>
            </div>
            <h3 className="my-1 font-bold">{t("measurement_information")}:</h3>
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem">
                {t("station_name")}:
              </h3>
              <span className="device-details ml-7">
                {headerData["station"]}
              </span>{" "}
            </div>
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem">{t("company")}:</h3>
              <span className="device-details ml-7">
                {headerData["company"]}
              </span>{" "}
            </div>
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem"> {t("client")}:</h3>
              <span className="device-details ml-7">
                {headerData["client"]}
              </span>
            </div>
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem"> {t("location")}:</h3>
              <span className="device-details ml-7">
                {headerData["location"]}
              </span>
            </div>
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem">
                {" "}
                {t("gps_position")}:
              </h3>
              <span className="device-details ml-7">{gpsPos}</span>
            </div>
            <div className="flex align-items-center">
              <h3 className="device-details my-2 w-12rem"> {t("comment")}:</h3>
            </div>
          </div>
          <div className="lg:col-6">
            <h3 className="my-1 font-bold ml-4">
              {t("devices")} {t("information")}:
            </h3>
            <div className="flex align-items-center ml-4">
              <h3 className="device-details my-2 w-12rem">{t("devices")}:</h3>
              <span className="device-details ml-5"> {deviceName}</span>
            </div>
            <div className="flex align-items-center ml-4">
              <h3 className="device-details my-2 w-12rem"> SN# :</h3>
              <span className="device-details ml-5"> {deviceId}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid justify-content-end">
        {/*<div className="lg:col-6">*/}
        {/*  <h2 className="ml-2">{t("comment")}</h2>*/}
        {/*</div>*/}
        {dinVisible ? (
          <div
            className="highchart-din lg:col-6"
            style={{ marginTop: "-175px", marginRight: "80px" }}
          >
            <HighchartsReact highcharts={Highcharts} options={scatter_graph} />
          </div>
        ) : (
          <div className="highchart-din"></div>
        )}
      </div>

      <div className="highcharts-div">
        {/* <div className="grid" style={{ lineHeight: "0.2" }}>
          <div className="col-md-2 ml-8">
            <h4>{t("channel_name")}</h4>
          </div>
          <div className="col-md-2 ml-4">
            <h4>|{t("maxima")}|</h4>
          </div>
          <div className="col-md-2 ml-4">
            <h4>{t("time_maxima")}</h4>
          </div>
          <div className="col-md-2 ml-4">
            <h4>KBFmax</h4>
          </div>
          <div className="col-md-2 ml-4">
            <h4>FFT {t("frequency")}</h4>
          </div>
          <div className="col-md-2 ml-4">
            <h4>{t("peak_displacement")}</h4>
          </div>
          <div className="col-md-2 ml-4">
            <h4>{t("peak_acceleration")}</h4>
          </div>
        </div> */}

        {/* <div className="grid" style={{ lineHeight: "0.2" }}>
          <p className="">K1(X)</p>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              : X
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-7" style={{ textAlign: "right" }}>
              {maxX} {xAxisUnit}
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-5" style={{ textAlign: "right" }}>
              {maxTimeX} s
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              ---
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              {frequenceX} Hz
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-7" style={{ textAlign: "right" }}>
              {integrationX} µm
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-8" style={{ textAlign: "right" }}>
              {differentialX} mm/s²
            </p>
          </div>
        </div>
        <div className="grid" style={{ lineHeight: "0.2" }}>
          <p className="">K1(Y)</p>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              : Y
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-7" style={{ textAlign: "right" }}>
              {maxY} {yAxisUnit}
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-5" style={{ textAlign: "right" }}>
              {maxTimeY} s
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              ---
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              {frequenceY} Hz
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-7" style={{ textAlign: "right" }}>
              {integrationY} µm
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-8" style={{ textAlign: "right" }}>
              {differentialY} mm/s²
            </p>
          </div>
        </div>

        <div className="grid" style={{ lineHeight: "0.2" }}>
          <p className="">K1(Z)</p>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              : Z
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-7" style={{ textAlign: "right" }}>
              {maxZ} {zAxisUnit}
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-5" style={{ textAlign: "right" }}>
              {maxTimeZ} s
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              ---
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-6" style={{ textAlign: "right" }}>
              {frequenceZ} Hz
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-7" style={{ textAlign: "right" }}>
              {integrationZ} µm
            </p>
          </div>
          <div className="col-md-2 ml-4">
            <p className="ml-8" style={{ textAlign: "right" }}>
              {differentialZ} mm/s²
            </p>
          </div>
        </div> */}

        <div className="grid-container">
          {/* <!-- Header Row --> */}
          <div className="grid-item header">{t("channel_name")}</div>
          <div className="grid-item header">|{t("maxima")}|</div>
          <div className="grid-item header">{t("time_maxima")}</div>
          <div className="grid-item header">KBFmax</div>
          <div className="grid-item header">FFT {t("frequency")}</div>
          <div className="grid-item header">{t("peak_displacement")}</div>
          <div className="grid-item header">{t("peak_acceleration")}</div>

          {/* <!-- Data Row X --> */}
          <div className="grid-item">K1(X)</div>
          <div className="grid-item">
            {maxX} {xAxisUnit}
          </div>
          <div className="grid-item">{maxTimeX} s</div>
          <div className="grid-item">---</div>
          <div className="grid-item">{frequenceX} Hz</div>
          <div className="grid-item">{integrationX} µm</div>
          <div className="grid-item">{differentialX} mm/s²</div>
          {/* 
  <!-- Data Row Y --> */}
          <div className="grid-item">K1(Y)</div>
          <div className="grid-item">
            {maxY} {yAxisUnit}
          </div>
          <div className="grid-item">{maxTimeY} s</div>
          <div className="grid-item">---</div>
          <div className="grid-item">{frequenceY} Hz</div>
          <div className="grid-item">{integrationY} µm</div>
          <div className="grid-item">{differentialY} mm/s²</div>

          {/* <!-- Data Row Z --> */}
          <div className="grid-item">K1(Z)</div>
          <div className="grid-item">
            {maxZ} {zAxisUnit}
          </div>
          <div className="grid-item">{maxTimeZ} s</div>
          <div className="grid-item">---</div>
          <div className="grid-item">{frequenceZ} Hz</div>
          <div className="grid-item">{integrationZ} µm</div>
          <div className="grid-item">{differentialZ} mm/s²</div>
        </div>

        <div className="mt-6">
          <HighchartsReact highcharts={Highcharts} options={graph_x} />
          <HighchartsReact highcharts={Highcharts} options={graph_y} />
          <HighchartsReact highcharts={Highcharts} options={graph_z} />
        </div>
      </div>
    </div>
  );
};

export default SingleReport;
